import axios from '@/plugins/axios';
import { useToast } from '@/composables/use-toast.js';

const { addToast } = useToast();
const baseUrl = '/api/files';
const apiURL = axios.defaults.baseURL || 'http://localhost:3000';
const acceptFormatsUrl = `${baseUrl}/accepted-file-formats`;

/**
 * Get the headers for an upload request
 * @returns {Object} - The headers
 */
const getUploadHeaders = () => {
    const token = localStorage.getItem('authToken');
    const headers = {
        'Accept': 'application/json'
    };
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
};

/**
 * Get the accepted file formats from the server
 * @returns {Promise<Object>} - The response from the API
 * @throws {Error} - If the request fails
 */
export const getAcceptedFileFormats = async () => {
    const response = await axios.get(acceptFormatsUrl);
    return response;
}

/**
 * Create an accepted file format on the server
 * @param {Object} data - The data to send with the request
 * @returns {Promise<Object>} - The response from the API
 * @throws {Error} - If the request fails
 */
export const createAcceptedFileFormat = async (data) => {
    try {
        const response = await axios.post(acceptFormatsUrl, data);
        return response;
    } catch (error) {
        if(error.status === 409) {
          return {error: true, message: error.response.data.message || 'Error to add format'};
        }
    }
}

/**
 * Delete an accepted file format from the server
 * @param {number} id - The ID of the accepted file format to delete
 * @returns {Promise<Object>} - The response from the API
 * @throws {Error} - If the request fails
 */
export const deleteAcceptedFileFormat = async (id) => {
    const response = await axios.post(`${acceptFormatsUrl}/${id}/delete`);
    return response;
}

/**
 * Initiate an upload of a file to the server
 * @param {Object} headers - The headers to send with the request
 * @param {string} fileName - The name of the file
 * @param {number} fileSize - The size of the file
 * @param {number} ownerId - The ID of the owner of the file
 * @returns {Promise<Object>} - The response from the API
 * @throws {Error} - If the upload fails
 */
export const initiateUpload = async (headers, fileName, fileSize, ownerId) => {
    const startResponse = await fetch(`${apiURL}${baseUrl}/chunk/start`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        ...headers,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        name: fileName,
        size: fileSize,
        ownerId: Number(ownerId)
      })
    });
  
    if (!startResponse.ok) {
      const errorData = await startResponse.json();
      if (startResponse.status === 409) {
        addToast(errorData.message, 'error');
        return { error: 'FILE_EXISTS', message: errorData.message };
      }
      addToast(errorData.message, 'error');
      throw new Error('Failed to start upload');
    }
  
    const startResponseData = await startResponse.json();
    if (startResponseData.status !== 201) {
      return { error: startResponseData.message };
    }
  
    return startResponseData;
};
  
/**
 * Upload a chunk of a file to the server
 * @param {Object} headers - The headers to send with the request
 * @param {Blob} chunk - The chunk of the file to upload
 * @param {Object} uploadData - The data to send with the request
 * @returns {Promise<Object>} - The response from the API
 * @throws {Error} - If the upload fails
 */
export const uploadChunk = async (headers, chunk, uploadData) => {
    const formData = new FormData();
    formData.append('chunk', chunk);
    formData.append('uploadId', uploadData.uploadId);
    formData.append('chunkNumber', uploadData.chunkNumber.toString());
    formData.append('totalChunks', uploadData.totalChunks.toString());
    formData.append('filename', uploadData.fileName);
    formData.append('totalSize', uploadData.totalSize.toString());
    formData.append('fileType', uploadData.fileExtension);

    const chunkResponse = await fetch(`${apiURL}${baseUrl}/chunk/upload`, {
        method: 'POST',
        credentials: 'include',
        headers: headers,
        body: formData
    });

    if (!chunkResponse.ok) {
        throw new Error(`Error sending chunk ${uploadData.chunkNumber}`);
    }

    const chunkResponseData = await chunkResponse.json();
    return chunkResponseData;
};

/**
 * Parse the name and extension of a file
 * @param {string} originalName - The original name of the file
 * @returns {Object} - The name and extension of the file
 * @throws {Error} - If the parsing fails
 */
const parseFileName = (originalName) => {
    const parts = originalName.split('.');
    const fileName = parts[0];
    const fileExtension = parts.length > 2 ? `${parts[1]}.${parts[2]}` : parts[1];
    return { fileName, fileExtension };
};

/**
 * Upload a file to the server
 * @param {File} file - The file to upload
 * @param {number} ownerId - The ID of the owner of the file
 * @param {Function} onProgress - The function to call with the progress of the upload
 * @param {string} customName - The custom name of the file
 * @returns {Promise<Object>} - The response from the API
 * @throws {Error} - If the upload fails
 */
export const uploadFile = async (file, ownerId, onProgress, customName = null) => {
  const chunkMaxSize = 14;
  const chunkSize = chunkMaxSize * 1024 * 1024;
  const totalChunks = Math.ceil(file.size / chunkSize);
  const headers = getUploadHeaders();
  const finalFileName = customName || file.name;

  try {
    const startResult = await initiateUpload(headers, finalFileName, file.size, ownerId);
    if (startResult.error) return startResult;

    const { uploadId } = startResult;
    const { fileName, fileExtension } = parseFileName(finalFileName);

    for (let i = 0; i < totalChunks; i++) {
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const chunk = file.slice(start, end);

      const uploadChunkResponse = await uploadChunk(headers, chunk, {
        uploadId,
        chunkNumber: i + 1,
        totalChunks,
        fileName,
        totalSize: file.size,
        fileExtension
      });

      if (onProgress) {
        onProgress(((i + 1) / totalChunks) * 100);
      }
      if(uploadChunkResponse.completed){
        return uploadChunkResponse
      }
    }
    return startResult.status;
  } catch (error) {
    console.error('Error during upload:', error);
    throw error;
  }
};

/**
 * Validate the content of an archive file
 * @param {string} filePath - The path to the archive file
 * @returns {Promise<Object>} - The response from the API
 * @throws {Error} - If the validation fails
 */
export const validateArchiveFile = async (filePath, expectNames) => {
    try {
        const payload = Array.isArray(expectNames) && expectNames.length > 0
            ? { filePath, expect_names: expectNames }
            : filePath;
        const response = await axios.post(`${baseUrl}/validate-file-content`, payload);
        return response;
    } catch (error) {
        console.error('Error validating archive file:', error);
        throw error;
    }
}